import { Avatar, Theme } from "@mui/material"
import { Chip } from "@oreve/business-ui"
import React, { useState } from 'react';
import { ButtonProps } from "@mui/material/Button"
import { SystemStyleObject } from "@mui/system"

interface OreveChipInfoProps {
    label: string
    icon: React.ReactNode
}

export const OreveChipInfo = ({ label, icon }: OreveChipInfoProps) => {
    return (
        <Chip
            avatar={<Avatar sx={{ bgcolor: "primary.main" }}>{icon}</Avatar>}
            label={label}
            sx={{ bgcolor: "grey.100", typography: "labelLarge" }}
        />
    )
}

interface OreveChipButtonProps extends ButtonProps {
    label: string
    onClick: () => void
    isDefault?: boolean
    dataCy? : string
    sx?: SystemStyleObject<Theme> | ((theme: Theme) => SystemStyleObject<Theme>) | Array<SystemStyleObject<Theme> | ((theme: Theme) => SystemStyleObject<Theme>)>
}

export const OreveChipButton = ({
    label,
    onClick,
    isDefault = false,
    dataCy = "",
    sx = {}
}: OreveChipButtonProps) => {



    const [isSelected, setIsSelected] = useState(isDefault)

    const handleClick = () => {
        setIsSelected(prev => !prev)
        onClick?.()
    }

    return (
        <Chip
            label={label}
            sx={(theme) => {
                const chipPalette = (theme).palette?.oreveChipButton ?? {
                    bgSelected: theme.palette.primary.main,
                    bgUnselected: theme.palette.grey[100],
                    textSelected: theme.palette.common.white,
                    textUnselected: theme.palette.primary.main,
                    bgSelectedHover: theme.palette.primary.dark,
                    bgUnselectedHover: theme.palette.grey[200],
                }

                const base = {
                    bgcolor: chipPalette[isSelected ? "bgSelected" : "bgUnselected"],
                    color: chipPalette[isSelected ? "textSelected" : "textUnselected"],
                    border: '1px solid',
                    borderColor: chipPalette[isSelected ? "bgSelected" : "textUnselected"],
                    typography: 'labelLarge',
                    cursor: 'pointer',
                    flexShrink: 0,
                    '&:hover': {
                        bgcolor: chipPalette[isSelected ? "bgSelectedHover" : "bgUnselectedHover"],
                    },
                } as const

                type SxInput = SystemStyleObject<Theme> | ((theme: Theme) => SystemStyleObject<Theme>)
                let user: SystemStyleObject<Theme> = {}
                if (Array.isArray(sx)) {
                    user = (sx as Array<SxInput>).reduce<SystemStyleObject<Theme>>((acc, item) => {
                        const part = typeof item === 'function' ? item(theme) : (item || {})
                        return { ...acc, ...part }
                    }, {})
                } else if (typeof sx === 'function') {
                    user = (sx as (theme: Theme) => SystemStyleObject<Theme>)(theme)
                } else if (sx) {
                    user = sx as SystemStyleObject<Theme>
                }

                return { ...base, ...user }
            }}
            onClick={handleClick}
            data-cy={dataCy}
        />
    )
}

interface SelectableChipProps {
    label: string
    selected: boolean
    onClick: () => void
    sx?: SystemStyleObject<Theme> | ((theme: Theme) => SystemStyleObject<Theme>)
    disabled?: boolean
    dataCy?: string
}

export const SelectableChip: React.FC<SelectableChipProps> = ({
    label,
    selected,
    onClick,
    sx,
    disabled = false,
    dataCy
}: SelectableChipProps) => {
    return (
        <Chip
            label={label}
            onClick={onClick}
            disabled={disabled}
            aria-pressed={selected}
            data-cy={dataCy}
            sx={(theme) => {
                const chipPalette = (theme).palette?.oreveChipButton ?? {
                    bgSelected: theme.palette.primary.main,
                    bgUnselected: theme.palette.grey[100],
                    textSelected: theme.palette.common.white,
                    textUnselected: theme.palette.primary.main,
                    bgSelectedHover: theme.palette.primary.dark,
                    bgUnselectedHover: theme.palette.grey[200],
                }
                const base = {
                    bgcolor: chipPalette[selected ? "bgSelected" : "bgUnselected"],
                    color: chipPalette[selected ? "textSelected" : "textUnselected"],
                    borderRadius: "100px",
                    typography: "labelLarge",
                    cursor: disabled ? "not-allowed" : "pointer",
                    flexShrink: 0,
                    transition: "background-color 0.15s ease, color 0.15s ease",
                    "&:hover": {
                        bgcolor: chipPalette[selected ? "bgSelected" : "bgUnselectedHover"],
                    },
                } as const
                const user = typeof sx === "function" ? sx(theme) : (sx ?? {})
                return { ...base, ...user }
            }}
        />
    )
}